import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, CardBody, Card } from 'reactstrap';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { LinearProgress, Tooltip, Box, IconButton, Button, Checkbox, Typography } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PreviewIcon from '@mui/icons-material/Preview';
import MapIcon from '@mui/icons-material/Map';
import Badge from 'react-bootstrap/Badge';
import Select from 'react-select';

import RiderLocationMapModal from 'src/containers/PnD/RiderLocationHistory/View/components/RiderLocationMapModal';
import FreeRiderDetails from './components/FreeRiderComponents/FreeRiderDetails';
import OrderDetailsShippingViewDetailsModal from './components/OrderDetailsShippingViewDetailsModal';
import OrderTrackingModal from 'src/containers/PnD/Views/OrderView/OrderTrackingView/OrderTrackingModal';
import ConfirmationDialog from 'src/shared/components/ConfirmationDialog';

import {
  HUB_MANAGER_ROLE_ID,
  HUB_OPERATOR_ROLE_ID,
  MERCHANT_ROLE_ID,
  OPERATION_TYPE_FOR_PICKUP_ASSIGNED,
  OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED,
  OPERATION_TYPE_FOR_BULK_ORDER_ASSIGNMENT,
  ERROR_MESSAGES
} from 'src/utils/pnd/PndConstants';
import { showNotification } from 'src/shared/components/Notification';
import { formatDate, safeFormatDateOnly } from 'src/utils/DateUtils';
import PageHeader from 'src/shared/components/layout/PageHeader';
import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';

const OrderReassignDetailsIndex = () => {
  const [reactTableData, setReactTableData] = useState({ tableRowsData: [] });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [orderTypeFilter, setOrderTypeFilter] = useState({ value: 0, label: "All" });

  const [loading, setLoading] = useState(false);
  const [isNoRecordFound, setNoRecordFound] = useState(false);
  const [temp, setTempData] = useState("");
  const [viewDetailPopup, setViewDetailPopup] = useState(false);
  const [isTitle, setTitle] = useState(null);
  const [isGoogleMapView, setGoogleMapView] = useState(null);
  const [isAssignOrderPopup, setAssignOrderPopup] = useState(false);
  const [isOrderNo, setOrderNo] = useState(null);
  const [isOrderId, setOrderId] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isOperationType, setOperationType] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [isStatusProgress, setStatusProgress] = useState(false);
  const [isLocation, setLocation] = useState({ latitude: null, longitude: null });
  const [isOrderType, setOrderType] = useState(null);
  const [isRiderId, setRiderId] = useState(null);

  useEffect(() => {
    getAllAssignedOrderDetails(orderTypeFilter.value);
  }, []);

  const getAllAssignedOrderDetails = async (type = 0, orderPriorityId = 0) => {
    try {
      setLoading(true);
      setNoRecordFound(false);
      let Obj = {};
      Obj["orderType"] = type;
      Obj["orderPriorityTypeId"] = orderPriorityId;

      const response = await PndRestApi.GetAllAssignedOrders(Obj);
      if (response.status === 200 && response.data.status === 200) {
        const ordersList = response.data?.data || [];
        setReactTableData({ tableRowsData: ordersList });
        if (ordersList.length > 0) {
          setSelectedOrder(ordersList[0]);
        } else {
          setSelectedOrder(null);
        }
      } else if (response?.data?.status && response?.data?.status === 204) {
        setNoRecordFound(true);
        setReactTableData({ tableRowsData: [] });
        setSelectedOrder(null);
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `SuccessCode: ${response.data.status}`,
          `Message:${response.data.message}`,
        );
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification(
        "theme-light",
        "ltr",
        `SuccessCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxClick = (row) => {
    if (row && row.orderId && row.orderNumber && row.orderType) {
      const { orderId, orderNumber, orderType, riderId, ratsId } = row;
      let orderStatus = orderType === 1 ? OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED : OPERATION_TYPE_FOR_PICKUP_ASSIGNED;

      setSelectedOrderIds((prevIds) => {
        if (prevIds.some(({ id }) => id === orderId)) {
          return prevIds.filter(({ id }) => id !== orderId);
        } else {
          return [...prevIds, { id: orderId, orderNumber, orderType, orderStatus, riderId, ratsId }];
        }
      });
    }
  };

  const openMap = async (event) => {
    if (event) {
      setLocation({ latitude: event?.latitude, longitude: event?.longitude });
      setTempData(event);
      setGoogleMapView(true);
    }
  };

  const viewDetails = async (event) => {
    if (event?.orderId) {
      setTempData(event);
      setViewDetailPopup(true);
      setTitle("View-Order Details");
    }
  };

  const processOrder = async (event, operationType) => {
    if (selectedOrderIds.length === 0 && localStorage.getItem("uri") === HUB_MANAGER_ROLE_ID && !event) {
      showNotification(
        "theme-light",
        "ltr",
        `SuccessCode: 500`,
        `Message:Please select the Order first.`,
      );
      return;
    }
    if (event && operationType && event?.riderId) {
      setOrderNo(event?.orderNumber);
      setOrderId(event?.orderId);
      setRiderId(event?.riderId);
      setOperationType(operationType);
      setSelectedDriverId(null);
      setAssignOrderPopup(true);
      setOrderType(event?.orderType);
    }
  };

  const showDialogBox = () => {
    if (selectedOrderIds.length === 0) {
      showNotification(
        "theme-light",
        "ltr",
        `SuccessCode: 500`,
        `Message:Please select the Order first.`,
      );
      return;
    }
    setSelectedDriverId(null);
    setAssignOrderPopup(true);
  };

  const handleRedirect = () => {
    setSelectedOrderIds([]);
    getAllAssignedOrderDetails(orderTypeFilter.value);
  };

  const closeDialog = () => {
    setShowConfirmation(false);
  };

  const orderTypeOptions = useMemo(() => {
    const role = localStorage.getItem("uri");
    if (role === HUB_MANAGER_ROLE_ID) {
      return [
        { value: 0, label: "All (Pick Up/Drop Off)" },
        { value: 1, label: "Drop Off" },
        { value: 2, label: "Pick Up" },
        { value: 3, label: "Pick and Drop" },
      ];
    } else if (role === HUB_OPERATOR_ROLE_ID) {
      return [
        { value: 0, label: "All" },
        { value: 1, label: "Drop Off" },
        { value: 2, label: "Pick Up" },
        { value: 3, label: "Pick & Drop" },
        { value: 4, label: "Pick Or Drop" },
      ];
    } else if (role === MERCHANT_ROLE_ID) {
      return [
        { value: 0, label: "All" },
        { value: 2, label: "Pick Up" },
        { value: 3, label: "Pick & Drop" },
      ];
    }
    return [
      { value: 0, label: "All" },
      { value: 1, label: "Drop Off" },
      { value: 2, label: "Pick Up" },
      { value: 3, label: "Pick & Drop" },
    ];
  }, []);

  const ORDER_COLUMNS = useMemo(() => [
    {
      key: 'checkbox',
      header: 'Select',
      width: '60px',
      render: (val, row) => (
        <Checkbox
          checked={selectedOrderIds.some(item => item.id === row.orderId)}
          onChange={(e) => {
            e.stopPropagation();
            handleCheckboxClick(row);
          }}
          onClick={(e) => e.stopPropagation()}
          size="small"
        />
      )
    },
    {
      key: 'orderNumber',
      header: 'Order No.',
      sortable: true,
      width: '140px',
    },
    {
      key: 'erpOrderNo',
      header: 'ERP No.',
      sortable: true,
      width: '130px',
    },
 {
     key: 'requestedDateTimeSlot',
     header: 'Req. Date',
     sortable: true,
     width: '135px',
     render: (value) => value ? safeFormatDateOnly(value) : '—',
   },

    {
      key: 'reqTimeSlot',
      header: 'Time Slot',
      sortable: true,
      width: '150px',
    },
    {
      key: 'areaName',
      header: 'Area',
      sortable: true,
      width: '160px',
    },
    {
      key: 'orderStatus',
      header: 'Status',
      sortable: true,
      width: '130px',
    },
    {
      key: 'riderName',
      header: 'Rider',
      sortable: true,
      width: '150px',
    },
  ], [selectedOrderIds]);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Container fluid style={{ maxWidth: '1400px' }}>
        <PageHeader
          icon={<AssignmentIcon fontSize="large" />}
          title="Reassign Order"
          subtitle="Manage active order assignments and dispatching."
          gradient="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
        />

        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

        <Row style={{ marginTop: '20px' }}>
          {/* Left Panel: Master List */}
          <Col md={8} lg={8}>
            {/* Filter and Action Bar */}
            <Card className="card--not-full-height" style={{ borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px', padding: '16px' }}>
              <Row className="align-items-center">
                <Col md={6}>
                  <div style={{ marginBottom: '8px' }}>
                    <span className="small text-muted fw-bold">Order Type</span>
                    <Select
                      value={orderTypeFilter}
                      options={orderTypeOptions}
                      placeholder="Select Order Type"
                      onChange={(val) => {
                        setOrderTypeFilter(val);
                        getAllAssignedOrderDetails(val.value);
                      }}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          borderRadius: '8px',
                          borderColor: '#cbd5e1',
                          minHeight: '38px',
                        })
                      }}
                    />
                  </div>
                </Col>
                <Col md={6} className="d-flex justify-content-end align-items-center" style={{ height: '100%' }}>
                  {localStorage.getItem('uri') === HUB_MANAGER_ROLE_ID && (
                    <Button
                      variant="contained"
                      disabled={selectedOrderIds.length === 0}
                      onClick={showDialogBox}
                      sx={{
                        backgroundColor: selectedOrderIds.length > 0 ? '#10b981' : '#cbd5e1',
                        '&:hover': {
                          backgroundColor: '#059669',
                        },
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        py: '8px',
                      }}
                    >
                      Reassign Selected ({selectedOrderIds.length})
                    </Button>
                  )}
                </Col>
              </Row>
            </Card>

            {!isNoRecordFound ? (
              <RadioSelectTable
                rows={reactTableData.tableRowsData || []}
                columns={ORDER_COLUMNS}
                rowKey="orderId"
                selectedKey={selectedOrder?.orderId}
                onSelect={setSelectedOrder}
                searchKeys={['orderNumber', 'riderName']}
                searchPlaceholder="Search orders..."
                pageSizes={[5, 10, 20, 50]}
                minWidth="850px"
              />
            ) : (
              <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', padding: '40px', textAlign: 'center' }}>
                <p className="text-muted m-0">No records found.</p>
              </Card>
            )}
          </Col>

          {/* Right Panel: Sleek Detail Card */}
          <Col md={4} lg={4}>
            <Card className="card--not-full-height" style={{
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
              minHeight: '520px',
              background: '#ffffff',
              overflow: 'hidden',
            }}>
              {selectedOrder ? (
                <>
                  {/* Header Banner */}
                  <div style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    padding: '24px 20px',
                    color: '#ffffff'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                          {selectedOrder.orderNumber}
                        </h4>
                        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                          Priority: {selectedOrder.orderPriorityName || 'Standard'}
                        </span>
                      </div>
                      <div>
                        <span
                          className="badge bg-white text-primary"
                          style={{ fontSize: '0.75rem', fontWeight: 700, padding: '5px 10px', borderRadius: '12px' }}
                        >
                          {selectedOrder.orderStatus || 'ASSIGNED'}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                      <Tooltip title="View Order Details">
                        <IconButton
                          size="small"
                          onClick={() => viewDetails(selectedOrder)}
                          style={{ color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reassign Order">
                        <IconButton
                          size="small"
                          onClick={() => processOrder(selectedOrder, OPERATION_TYPE_FOR_PICKUP_ASSIGNED)}
                          style={{ color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <AssignmentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  {/* Details Body */}
                  <CardBody style={{ padding: '20px', display: 'flex', flexDirection: 'column', minHeight: '400px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                      {/* General Info */}
                      <div>
                        <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                          General Info
                        </h6>
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Order Type:</span>
                            <span className="fw-bold small text-dark">
                              {selectedOrder.orderType === 1 ? (
                                <Badge bg="success">DO</Badge>
                              ) : selectedOrder.orderType === 2 ? (
                                <Badge bg="primary">PU</Badge>
                              ) : (
                                <Badge bg="warning">PnD</Badge>
                              )}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Scheduled Date:</span>
                            <span className="fw-bold small text-dark">
                              {selectedOrder.scheduledDateTimeSlot ? formatDate(selectedOrder.scheduledDateTimeSlot) : '—'}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="text-muted small">Rider Assigned:</span>
                            <span className="fw-bold small text-dark">{selectedOrder.riderName || '—'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Logistics Info */}
                      <div>
                        <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                          Logistics & Route
                        </h6>
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted small">Delivery Location:</span>
                            {selectedOrder.areaName ? (
                              <Tooltip title="View in Map">
                                <IconButton size="small" onClick={() => openMap(selectedOrder)} style={{ color: '#2563eb' }}>
                                  <MapIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </div>
                          <span className="fw-bold small text-dark">{selectedOrder.areaName || '—'}</span>
                        </div>
                      </div>

                    </div>

                    {/* Footer Action Button */}
                    <Box sx={{ mt: 'auto', pt: 3 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<AssignmentIcon />}
                        onClick={() => processOrder(selectedOrder, OPERATION_TYPE_FOR_PICKUP_ASSIGNED)}
                        sx={{
                          textTransform: 'none',
                          borderRadius: '8px',
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                          boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.25)',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                          color: '#ffffff',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                            boxShadow: '0 6px 10px -1px rgba(59, 130, 246, 0.35)',
                          }
                        }}
                      >
                        REASSIGN ORDER
                      </Button>
                    </Box>
                  </CardBody>
                </>
              ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
                }}>
                  <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>📦</div>
                  <h5 className="fw-bold text-muted">No Order Selected</h5>
                  <p className="text-muted small">Select an order from the list to view detailed information.</p>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Container>

      {isGoogleMapView && (
        <RiderLocationMapModal
          modalShow={isGoogleMapView}
          setModalShow={setGoogleMapView}
          data={isLocation}
        />
      )}

      {isAssignOrderPopup && (
        <FreeRiderDetails
          setSelectedDriverId={setSelectedDriverId}
          selectedDriverId={selectedDriverId}
          modalShow={isAssignOrderPopup}
          setRedirect={() => handleRedirect()}
          setModalShow={setAssignOrderPopup}
          isOrderNo={isOrderNo}
          isOrderId={isOrderId}
          isOperationType={isOperationType || OPERATION_TYPE_FOR_BULK_ORDER_ASSIGNMENT}
          selectedOrderIds={selectedOrderIds}
          isOrderType={isOrderType}
          isRiderId={isRiderId}
          data={reactTableData.tableRowsData}
        />
      )}

      {showConfirmation && (
        <ConfirmationDialog
          open={showConfirmation}
          onClose={closeDialog}
          onConfirm={() => processOrder(temp)}
          title="Confirmation"
          message="Are you sure you want to proceed?"
        />
      )}

      {viewDetailPopup && (
        <OrderDetailsShippingViewDetailsModal
          modalShow={viewDetailPopup}
          setModalShow={setViewDetailPopup}
          orderId={temp?.orderId}
          isTitle={isTitle}
        />
      )}

      {isStatusProgress && (
        <OrderTrackingModal
          modalShow={isStatusProgress}
          isOrderId={isOrderId}
          isOrderNo={isOrderNo}
          setModalShow={setStatusProgress}
        />
      )}
    </Box>
  );
};

export default OrderReassignDetailsIndex;
